import React, { useEffect, useState } from 'react';
import { KeyRound, ExternalLink, Loader2, Check, X, AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from '../hooks/use-toast';

// Every buyer brings their own provider accounts, so this is theirs, not the
// operator's admin panel. The generation their keys pay for is their own, and
// nobody else's request can reach them.
export default function Keys() {
  const [rows, setRows] = useState([]);
  const [masked, setMasked] = useState({});
  const [fallback, setFallback] = useState([]);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [results, setResults] = useState({});

  const load = async () => {
    try {
      const r = await api.myKeys();
      // One row per provider, taken from the capability map so a provider added
      // later shows up here without this file changing.
      const byId = {};
      Object.values(r.providers || {}).forEach((cap) => {
        (cap.providers || []).forEach((p) => { byId[p.id] = p; });
      });
      setRows(Object.values(byId));
      setMasked(r.keys_masked || {});
      setFallback(r.falls_back_to_instance || []);
    } catch (e) {
      toast({ title: 'Could not load your keys', description: e.message });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.saveMyKeys({ provider_keys: values });
      setMasked(r.keys_masked || {});
      setValues({});
      await load();
      toast({ title: 'Saved', description: 'Your keys are stored and will be used for your work.' });
    } catch (e) {
      toast({ title: 'Could not save', description: e.message });
    } finally { setSaving(false); }
  };

  const test = async (id) => {
    setTesting(id);
    try {
      const res = await api.testMyKey(id);
      setResults((prev) => ({ ...prev, [id]: res }));
    } catch (e) {
      setResults((prev) => ({ ...prev, [id]: { ok: false, message: e.message } }));
    } finally { setTesting(null); }
  };

  return (
    <div className="min-h-screen bg-[#0E0E10] text-white">
      <main className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="flex items-center gap-2 font-serif-display text-3xl">
          <KeyRound size={20} className="text-node-model" /> API Keys
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-white/60">
          Idolaris runs on your own provider accounts. You create the keys, the usage is billed
          to you at their rates, and nothing you generate touches anyone else&apos;s account. You
          do not need all of them: one that covers video and one that covers images is enough
          to work.
        </p>

        {fallback.length > 0 && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-400" />
            <p className="text-[11px] leading-relaxed text-white/70">
              You have not added a key for {fallback.join(', ')} yet, so that work runs on this
              instance&apos;s shared account. Add your own to be independent of it.
            </p>
          </div>
        )}

        <div className="mt-6 space-y-3">
          {rows.map((p) => {
            const res = results[p.id];
            return (
              <div key={p.id} className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium text-white">{p.label}</span>
                  <a href={p.console_url} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 text-[11px] text-node-model hover:underline">
                    Get a key <ExternalLink size={10} />
                  </a>
                </div>
                <p className="mt-1 text-[11px] leading-relaxed text-white/45">{p.note}</p>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    type="password"
                    value={values[p.id] ?? ''}
                    onChange={(e) => setValues((v) => ({ ...v, [p.id]: e.target.value }))}
                    placeholder={masked[p.id] ? `saved: ${masked[p.id]}` : 'paste your key'}
                    data-testid={`key-${p.id}`}
                    className="min-w-0 flex-1 rounded-md border border-[#2A2A30] bg-[#0E0E10] px-3 py-2 text-sm text-white/85 focus:border-node-model focus:outline-none"
                  />
                  <button onClick={() => test(p.id)} disabled={testing === p.id}
                    data-testid={`test-${p.id}`}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[#2A2A30] px-3 py-2 text-[11px] text-white/80 hover:border-white/40 disabled:opacity-50">
                    {testing === p.id ? <Loader2 size={12} className="animate-spin" /> : null}
                    Test
                  </button>
                </div>

                {masked[p.id] && (
                  <p className="mt-1.5 text-[10px] text-white/35">
                    Empty the box and save to remove your key.
                  </p>
                )}
                {res && (
                  <p className={`mt-2 flex items-start gap-1.5 text-[11px] ${res.ok ? 'text-emerald-300' : 'text-red-300'}`}>
                    {res.ok ? <Check size={12} className="mt-0.5 shrink-0" /> : <X size={12} className="mt-0.5 shrink-0" />}
                    {res.message}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button onClick={save} disabled={saving || Object.keys(values).length === 0}
            data-testid="keys-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2 text-sm font-medium text-black hover:bg-white/90 disabled:opacity-40">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            Save keys
          </button>
          <span className="text-[11px] text-white/40">
            Stored against your account only. They are never shown back in full.
          </span>
        </div>
      </main>
    </div>
  );
}
